import React, {useRef} from 'react';
import {Avatar, Icon} from 'pepsico-ds';
import {TypewriterMessage} from './TypewriterMessage';
import {MarkdownRenderV2} from './MarkdownRenderV2';
import {ChartCard} from './ChartCard';
import {ChatChartSpec} from './chartUtils';
import {SupervisorAgentTrace} from './SupervisorAgentTrace';
import {LiveAgentTrace} from './LiveAgentTrace';
import {AgentOutputsPanel} from './AgentOutputsPanel';
import MessageMeta from './MessageMeta';
import {
  AiContent,
  AiIconWrap,
  AiMessageWrap,
  MessageRow,
  UserBubble,
  UserMessageWrap,
  AiIconWrapTransparent,
  DataTableWrap,
  DataTableLabel,
  CaveatText,
  ErrorText,
} from './ChatMessageItem.style';

interface ChatMessageItemProps {
  sender: string;
  content: string;
  userName: string;
  isFullScreen?: boolean;
  msg?: any;
}

const AI_LOADING_MESSAGES = [
  'Connecting to the data warehouse…',
  'Authenticating your session…',
  'Parsing your request…',
  'Identifying relevant data sources…',
  'Running data query…',
  'Retrieving records from the database…',
  'Applying filters and business rules…',
  'Cross-referencing related data sets…',
  'Aggregating results…',
  'Validating data integrity…',
  'Formatting your response…',
  'Performing final calculations…',
  'Preparing your answer…',
  'Almost there — finalizing output…',
  'Your results are ready.',
  'Re-analyzing copied results',
];
const AI_LOADING_LOOP_START = 11;
const AI_LOADING_SLOT_MS = 5000;

export const ChatMessageItem: React.FC<ChatMessageItemProps> = ({
  sender,
  content,
  userName,
  isFullScreen,
  msg,
}) => {
  const isUser = sender === 'user';
  const isLoadingMessage = content.trim() === 'Thinking...';
  const contentRef = useRef<HTMLDivElement>(null);
console.log('msg in ChatMessageItem', msg);
  if (isUser) {
    return (
      <MessageRow
        isUser
        isFullScreen={isFullScreen}
      >
        <UserMessageWrap isFullScreen={isFullScreen}>
          <UserBubble>{content}</UserBubble>
          <Avatar
            size='medium'
            type='icon'
            name={userName || 'U'}
            avatarStyle='circle'
            onClick={() => {}}
          />
        </UserMessageWrap>
      </MessageRow>
    );
  }

  return (
    <MessageRow
      isUser={false}
      isFullScreen={isFullScreen}
    >
      <AiMessageWrap isFullScreen={isFullScreen}>
        {/* For loading message: transparent background with dark icon */}
        {isLoadingMessage ? (
          <AiIconWrapTransparent>
            <Icon
              icon='auto_awesome'
              size='small'
              alt='AI'
              color='default'
            />
          </AiIconWrapTransparent>
        ) : (
          /* For regular messages: green background with white icon */
          <AiIconWrap>
            <Icon
              icon='auto_awesome'
              size='small'
              alt='AI'
              color='inverse'
            />
          </AiIconWrap>
        )}
        <AiContent>
          {isLoadingMessage ? (
            <TypewriterMessage
              messages={AI_LOADING_MESSAGES}
              loopStart={AI_LOADING_LOOP_START}
              slotMs={AI_LOADING_SLOT_MS}
            />
          ) : (
            <div ref={contentRef}>
              {msg?.agentTrace ? (
                <SupervisorAgentTrace msg={msg} />
              ) : (
                <>
                  <LiveAgentTrace trace={msg?.liveTrace} />

                  {msg?.isError ? (
                    <ErrorText>{content}</ErrorText>
                  ) : (
                    <MarkdownRenderV2 content={content} />
                  )}

                  {msg?.data && (
                    <DataTableWrap>
                      <DataTableLabel>Supporting data</DataTableLabel>
                      <MarkdownRenderV2 content={msg.data} />
                    </DataTableWrap>
                  )}

                  {Array.isArray(msg?.charts) &&
                    msg.charts.map((chart: ChatChartSpec, index: number) => (
                      <ChartCard
                        key={`${msg?.request_id || 'chart'}-${index}`}
                        chart={chart}
                      />
                    ))}

                  {!msg?.isError &&
                    msg?.outcome &&
                    msg.outcome !== 'success' &&
                    Array.isArray(msg?.caveats) &&
                    msg.caveats.length > 0 && (
                      <CaveatText>{msg.caveats.join(' · ')}</CaveatText>
                    )}

                  <AgentOutputsPanel
                    findings={msg?.detailedFindings}
                    sources={msg?.liveTrace?.sources}
                  />
                </>
              )}

              <MessageMeta msg={msg} />
            </div>
          )}
        </AiContent>
      </AiMessageWrap>
    </MessageRow>
  );
};
